import React from 'react';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import WarningBanner from '../WarningBanner/WarningBanner';
import {
  CONNECTION_STATUS,
  SHARED_MODE,
  COMPONENT_STATUS,
  WARNING_STATES,
} from '../../context/enums';
import { getActiveBannerMessage } from '../../utils/systemStatusUtils';
import { useSystem } from '../../context/SystemContext';
import './Layout.css';

/**
 * Layout component - Main layout wrapper that provides consistent structure across pages
 * @param {Object} props - Component properties
 * @param {React.ReactNode} props.children - Child components to render in the main content area
 * @returns {JSX.Element} The rendered layout with header, warning banner, main content, and footer
 */
const Layout = ({ children }) => {
  const {
    systemStatus, 
    sensors, 
    externalSensors,
    relays,
    connectionStatus
  } = useSystem();
  
  // Combine all sensors to check for threats
  const allSensors = [...(sensors || []), ...(externalSensors || [])];
  const allRelays = [...(relays || [])];
  
  // Detect error states from inactive sensors or relays
  const hasErrorStates = () => {
    // Check for inactive sensors
    const inactiveSensors = allSensors.filter(sensor => 
      sensor.status === COMPONENT_STATUS.INACTIVE
    );
    
    // Check for inactive relays
    const inactiveRelays = allRelays.filter(relay => 
      relay.status === COMPONENT_STATUS.INACTIVE
    );
    
    return {
      hasErrors: inactiveSensors.length > 0 || inactiveRelays.length > 0,
      inactiveSensors,
      inactiveRelays
    };
  };
  
  // Detect threats from sensor status
  const hasThreats = allSensors.some(sensor => {
    const status = (sensor.status || '').toLowerCase();
    
    // Check for threat indicators - if any sensor is active, there is a threat
    return status === COMPONENT_STATUS.ACTIVE;
  });

  // Determine banner status and message
  const getBannerState = () => {
    const errorState = hasErrorStates();
    
    // ========== ERROR PRIORITY ==========

    // Priority 0: Check if device is disconnected OR Fire controller has not posted any data to the bridge
    if (connectionStatus === CONNECTION_STATUS.DISCONNECTED || !systemStatus) {
      return WARNING_STATES.ERROR;
    }
    
    // Check for disconnected components (relays and/or sensors)
    const disconnectedRelays = allRelays.filter(relay => 
      relay.connection === CONNECTION_STATUS.DISCONNECTED
    );
    const disconnectedSensors = allSensors.filter(sensor => 
      sensor.connection === CONNECTION_STATUS.DISCONNECTED
    );
    
    if (disconnectedRelays.length > 0 || disconnectedSensors.length > 0) {
      const disconnectedComponents = [];
      
      if (disconnectedRelays.length > 0) {
        disconnectedComponents.push(`${disconnectedRelays.length} Relay(s)`);
      }
      if (disconnectedSensors.length > 0) {
        disconnectedComponents.push(`${disconnectedSensors.length} Sensor(s)`);
      }
      
      return {
        status: 'error',
        message: `System Error: ${disconnectedComponents.join(' and ')} Disconnected`
      };
    }
    
    // Priority 1: Check for component error states(inactive relays and sensors)
    if (errorState.hasErrors) {
      const errorComponents = [];
      if (errorState.inactiveRelays.length > 0) {
        errorComponents.push(`${errorState.inactiveRelays.length} relay(s)`);
      }
      if (errorState.inactiveSensors.length > 0) {
        errorComponents.push(`${errorState.inactiveSensors.length} sensor(s)`);
      }
      
      return {
        status: WARNING_STATES.ERROR.status,
        message: `System Error: ${errorComponents.join(' and ')} inactive - Immediate attention required`
      };
    }
    
    // TODO: Implement water level monitoring in future iteration
    // Check for water level empty
    // const waterSensor = allSensors.find(sensor => sensor.type === SENSOR_TYPES.WATER);
    // if (waterSensor && waterSensor.status) {
    //   const hoursMatch = waterSensor.status.match(/([\d.]+)/);
    //   if (hoursMatch) {
    //     const hours = parseFloat(hoursMatch[1]);
    //     if (hours === 0) {
    //       return WARNING_STATES.WATER_EMPTY;
    //     }
    //   }
    // }
    
    // ========== WARNING PRIORITY ==========
    
    // TODO: Implement water level monitoring in future iteration
    // Check for water level low
    // if (waterSensor && waterSensor.status) {
    //   const hoursMatch = waterSensor.status.match(/([\d.]+)/);
    //   if (hoursMatch) {
    //     const hours = parseFloat(hoursMatch[1]);
    //     if (hours > 0 && hours < 2) { // Low but not empty
    //       return WARNING_STATES.WATER_LOW;
    //     }
    //   }
    // }
    
    // Check for sensor active (threats detected but system not active)
    if (hasThreats) {
      const isSystemActive = systemStatus?.sharedMode === SHARED_MODE.ON || systemStatus?.sharedMode === SHARED_MODE.PRIMING;
      if (!isSystemActive) {
        return WARNING_STATES.WARNING;
      }
    }

    // Check for active system states (ON or PRIMING)
    const activeBannerMessage = getActiveBannerMessage(systemStatus);
    if (activeBannerMessage) {
      return { status: 'active', message: activeBannerMessage };
    }

    // Relay Active (when system status doesn't show active)
    const activeRelays = allRelays.filter(relay =>
      relay.status === COMPONENT_STATUS.ACTIVE
    );

    if (activeRelays.length > 0) {
      const isSystemActive = systemStatus?.sharedMode === SHARED_MODE.ON || systemStatus?.sharedMode === SHARED_MODE.PRIMING;
      if (!isSystemActive) {
        return WARNING_STATES.SYNC_WARNING;
      }
    }
    
    // TODO: Implement signal strength monitoring in future iteration
    // Signal strength low
    // const signalSensor = allSensors.find(sensor => sensor.type === SENSOR_TYPES.SIGNAL);
    // if (signalSensor && signalSensor.status === 'Weak') {
    //   return WARNING_STATES.SIGNAL_LOW;
    // }
    
    // ========== NOMINAL ==========
    
    // Normal state - System inactive, CBW Connected, sensors standby, relays standby
    return WARNING_STATES.NORMAL;
  };

  const { status: warningStatus, message: warningMessage } = getBannerState();
  
  return (
    <div className="layout">
      <Header />
      <WarningBanner status={warningStatus} message={warningMessage} />
      <main className="content">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default Layout;